import { Inject, Injectable, signal } from '@angular/core';
import { EMPTY, Observable, retry, take } from 'rxjs';
import { CrmInjectionToken, CrmDependencies, ProfileCard } from '../tokens-and-interfaces';
import { SNACKBAR_DURATION } from '../constants';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { map, switchMap } from 'rxjs/operators';
import { CreateAccountGroupForCompanyStatus } from '@vendasta/crm-integrations';
import { CompanyAccountGroupStatus } from './platform-api.service';
import { CrmPlatformApiService } from './platform-api.service';

@Injectable()
export class CompanyProfileCardsPanelService {
  public readonly companyProfileCards$: Observable<ProfileCard[]>;
  public loadingAccountGroup = signal(false);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private platformAPIService: CrmPlatformApiService,
    private snackbarService: SnackbarService,
  ) {
    this.companyProfileCards$ = this.config.company?.profileCards$ ?? EMPTY;
  }

  justInTimeAccountGroupCreation(companyId: string, afterCreatedCallback: (accountGroupId: string) => void): void {
    this.loadingAccountGroup.set(true);
    this.createAccountGroupForCompany(companyId)
      .pipe(take(1))
      .subscribe({
        next: (accountGroupId) => {
          if (accountGroupId.length > 0) {
            afterCreatedCallback(accountGroupId);
          }
          this.loadingAccountGroup.set(false);
        },
        error: () => {
          this.snackbarService.openErrorSnack('ASSOCIATIONS.COMPANY.ERROR', {
            duration: SNACKBAR_DURATION,
          });
          this.loadingAccountGroup.set(false);
        },
      });
  }

  //todo: make private function.
  createAccountGroupForCompany(companyId: string): Observable<string> {
    return this.platformAPIService.createAccountGroupForCompany(companyId).pipe(
      switchMap(() => this.waitForAccountGroupUpdate(companyId)),
      map((accountStatus) => {
        if (
          accountStatus.status === CreateAccountGroupForCompanyStatus.CREATE_ACCOUNT_GROUP_FOR_COMPANY_STATUS_SUCCEEDED
        ) {
          return accountStatus.accountGroupId;
        } else {
          throw new Error('Failed to create account group for company');
        }
      }),
    );
  }

  private waitForAccountGroupUpdate(companyId: string): Observable<CompanyAccountGroupStatus> {
    return this.platformAPIService.getCreateAccountGroupForCompanyStatus(companyId).pipe(
      map((accountStatus) => {
        if (
          accountStatus.status === CreateAccountGroupForCompanyStatus.CREATE_ACCOUNT_GROUP_FOR_COMPANY_STATUS_SUCCEEDED
        ) {
          // success.  Stop retrying.
          return { status: accountStatus.status, accountGroupId: accountStatus.accountGroupId };
        } else if (
          accountStatus.status === CreateAccountGroupForCompanyStatus.CREATE_ACCOUNT_GROUP_FOR_COMPANY_STATUS_FAILED
        ) {
          // failure.  Stop retrying.
          return { status: accountStatus.status, accountGroupId: '' };
        }
        // throw error to poll again
        throw false;
      }),
      retry({ delay: 1000 }),
    );
  }
}
